import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { countries } from "../../data/countries";
import { ChevronDown, X, Check } from "lucide-react";
import "./CountriesDropdown.css";

const CountriesDropdown = ({
  selectedCountries = [],
  setSelectedCountries,
  placeholder = "Select countries...",
  disabled = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);

  // Filter countries based on search
  const filteredCountries = countries.filter(
    (country) =>
      country.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      country.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Handle country selection
  const handleCountrySelect = (country) => {
    const countryName = t(country.name);

    if (selectedCountries.includes(countryName)) {
      setSelectedCountries(selectedCountries.filter((c) => c !== countryName));
    } else {
      setSelectedCountries([...selectedCountries, countryName]);
    }
  };

  // Handle select all
  const handleSelectAll = () => {
    const allCountries = filteredCountries.map((country) => t(country.name));
    setSelectedCountries(allCountries);
  };

  // Handle clear all
  const handleClearAll = () => {
    setSelectedCountries([]);
  };

  // Handle remove single country
  const handleRemoveCountry = (countryToRemove) => {
    setSelectedCountries(
      selectedCountries.filter((c) => c !== countryToRemove)
    );
  };

  // Close dropdown when clicking outside and handle touch events
  useEffect(() => {
    let isMounted = true;

    const handleClickOutside = (event) => {
      if (!isMounted) return;

      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    const handleTouchMove = (event) => {
      if (!isMounted) return;

      if (
        isOpen &&
        dropdownRef.current &&
        dropdownRef.current.contains(event.target)
      ) {
        // Prevent page scroll when scrolling within the dropdown
        event.stopPropagation();
      }
    };

    const handleTouchStart = (event) => {
      if (!isMounted) return;

      if (
        isOpen &&
        dropdownRef.current &&
        dropdownRef.current.contains(event.target)
      ) {
        // Prevent page scroll when touching within the dropdown
        event.stopPropagation();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });

    return () => {
      isMounted = false;
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchstart", handleTouchStart);
    };
  }, [isOpen]);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
      if (!isOpen) {
        setSearchTerm("");
      }
    }
  };

  const isSelected = (country) => selectedCountries.includes(t(country.name));

  return (
    <div
      className={`countries-dropdown ${className} ${disabled ? "disabled" : ""}`}
      ref={dropdownRef}
    >
      {/* Selected countries display */}
      <div className="selected-countries-container">
        {selectedCountries.length > 0 ? (
          <div className="selected-countries">
            {selectedCountries.map((country, index) => (
              <button
                key={`${country}-${index}`}
                type="button"
                className="remove-country-btn"
                onClick={() => handleRemoveCountry(country)}
                disabled={disabled}
                title={`Remove ${country}`}
              >
                <X size={12} />
                <span>{country}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="placeholder-text">{placeholder}</div>
        )}

        {selectedCountries.length > 0 && (
          <button
            type="button"
            className="clear-all-btn"
            onClick={handleClearAll}
            disabled={disabled}
            title="Remove all countries"
          >
            <X size={12} />
            <span>Remove all</span>
          </button>
        )}
      </div>

      {/* Dropdown toggle button */}
      <button
        type="button"
        className={`dropdown-toggle ${isOpen ? "open" : ""}`}
        onClick={toggleDropdown}
        disabled={disabled}
      >
        <span>{isOpen ? t("close_list") : t("select_countries")}</span>
        <ChevronDown className={`chevron ${isOpen ? "rotated" : ""}`} />
      </button>

      {/* Dropdown content */}
      {isOpen && (
        <div className="dropdown-content">
          {/* Search input */}
          <div className="search-container">
            <input
              type="text"
              className="search-input"
              placeholder={t("search_countries")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Action buttons */}
          <div className="dropdown-actions">
            <button
              type="button"
              className="action-btn select-all-btn"
              onClick={handleSelectAll}
              disabled={disabled}
            >
              <Check size={10} />
              {t("select_all_countries")}
            </button>
          </div>

          {/* Countries list */}
          <div className="countries-list">
            {filteredCountries.length > 0 ? (
              filteredCountries.map((country) => (
                <div
                  key={country.code}
                  className={`country-item ${isSelected(country) ? "selected" : ""}`}
                  onClick={() => handleCountrySelect(country)}
                >
                  <span className="country-flag">{country.flag}</span>
                  <span className="country-name">{t(country.name)}</span>
                  {isSelected(country) && (
                    <Check
                      className="check-icon"
                      size={12}
                    />
                  )}
                </div>
              ))
            ) : (
              <div className="no-results">{t("no_countries_found")}</div>
            )}
          </div>

          {/* Selection count */}
          {selectedCountries.length > 0 && (
            <div className="selection-count">
              {t("selected_count", { count: selectedCountries.length })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CountriesDropdown;
